import type { Parser } from 'bowser'

export const LOG_LEVEL_ERROR = <const>'error'
export const LOG_LEVEL_INFO = <const>'info'
export const LOG_LEVEL_WARN = <const>'warn'

export type LogLevel =
  | typeof LOG_LEVEL_ERROR
  | typeof LOG_LEVEL_INFO
  | typeof LOG_LEVEL_WARN

export const LOG_TYPE_REQUEST = <const>'request'
export const LOG_TYPE_BOUNDARY = <const>'boundary'
export const LOG_TYPE_CATCH = <const>'catch'
export const LOG_TYPE_INTL = <const>'intl'

type BaseLog = {
  message: string
  level: LogLevel
}

export type LoggableError = {
  name?: string
  message: string
  stack?: string
  status?: number
  statusText?: string
  url?: string
  code?: string
}

export type RequestErrorMetadata = {
  url: string
  statusCode: string
  /** Upstream error code, when the response body carries one. */
  errorCode?: string
}

type BoundaryErrorMetadata = {
  stack: string
  url: string
  browser: Parser.ParsedResult
  language?: string
  currency?: string
}

export type CatchErrorMetadata = {
  /** Stable identifier of the catchLogger / catch site that produced the log */
  id: string
  payload?: unknown
  cause?: LoggableError
}

type IntlWarningMetadata = {
  label: string
  language: string
  cause?: string
  stack?: string
}

export type RequestError = BaseLog & {
  type: typeof LOG_TYPE_REQUEST
  metadata: RequestErrorMetadata
}

export type BoundaryError = BaseLog & {
  type: typeof LOG_TYPE_BOUNDARY
  metadata: BoundaryErrorMetadata
}

export type CatchError = BaseLog & {
  type: typeof LOG_TYPE_CATCH
  metadata: CatchErrorMetadata
}

export type IntlWarning = BaseLog & {
  type: typeof LOG_TYPE_INTL
  metadata: IntlWarningMetadata
}

export type LogType = RequestError | BoundaryError | CatchError | IntlWarning

export type StructuredLog = Omit<LogType, 'level' | 'message'> & {
  account?: string
  workspace?: string
  app: string
}
